import React, { useState } from 'react';
import { Badge, Spinner, Form, Nav } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useIsSmallScreen } from '../../../hooks/useIsSmallScreen';
import ColumnInfoButton from './ColumnInfoButton';

interface FilterOption {
    value: string;
    label: string;
    count?: number;
}

interface ColumnCardProps {
    title: string;
    icon: React.ReactNode;
    count: number;
    loading: boolean;
    emptyMessage: string;
    viewAllLink?: string;
    onShowAll?: () => void;  // Callback alternative to viewAllLink
    children: React.ReactNode;
    /** When set, shows a small info icon next to the title with this text on hover. */
    titleInfo?: string;
    /** When set, renders a subheader bar below the column header (pass empty string for alignment spacer). */
    subtitle?: string;
    filterOptions?: FilterOption[];
    filterValue?: string;
    onFilterChange?: (value: string) => void;
    filterChips?: FilterOption[];
    filterChipValue?: string;
    onFilterChipChange?: (value: string) => void;
    /** Rendered at the bottom of the list, also when the list is empty (e.g. a "Visa alla" link). */
    footer?: React.ReactNode;
}

const ColumnCard: React.FC<ColumnCardProps> = ({
    title,
    icon,
    count,
    loading,
    emptyMessage,
    viewAllLink,
    onShowAll,
    children,
    titleInfo,
    subtitle,
    filterOptions,
    filterValue,
    onFilterChange,
    filterChips,
    filterChipValue,
    onFilterChipChange,
    footer
}) => {
    // On phones the columns stack, so the header collapses the list, like the
    // subcolumn headers in SubColumn.tsx.
    const isSmallScreen = useIsSmallScreen();
    const [open, setOpen] = useState(true);

    return (
        <div className={`management-column${subtitle !== undefined ? ' management-column--has-subtitle' : ''}`}>
            {isSmallScreen ? (
                <div className="management-column__header">
                    <button
                        type="button"
                        className="management-column__toggle"
                        aria-expanded={open}
                        onClick={() => setOpen(prev => !prev)}
                    >
                        {icon}
                        <span className="management-column__title">{title}</span>
                        <Badge bg="secondary">{count}</Badge>
                        <ChevronDown size={20} className="column-subcolumn-chevron" aria-hidden="true" />
                    </button>
                    {titleInfo && (
                        <ColumnInfoButton text={titleInfo} />
                    )}
                </div>
            ) : (
                <div className="management-column__header">
                    {icon}
                    <span className="management-column__title">{title}</span>
                    {titleInfo && (
                        <ColumnInfoButton text={titleInfo} />
                    )}
                    <Badge bg="secondary" className="ms-auto">{count}</Badge>
                </div>
            )}
            {(open || !isSmallScreen) && (
                <>
                    {subtitle !== undefined && (
                        <div className="column-subcolumn-header">{subtitle || ' '}</div>
                    )}
                    {filterOptions && filterOptions.length > 0 && (
                        <div className="management-column__filter">
                            <Form.Select
                                size="sm"
                                value={filterValue || ''}
                                onChange={(e) => onFilterChange?.(e.target.value)}
                            >
                                {filterOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </Form.Select>
                        </div>
                    )}
                    {filterChips && filterChips.length > 0 && (
                        <Nav variant="pills" className="management-column__chips gap-1">
                            {filterChips.map(chip => (
                                <Nav.Item key={chip.value}>
                                    <Nav.Link
                                        active={filterChipValue === chip.value}
                                        onClick={() => onFilterChipChange?.(chip.value)}
                                        className="py-1 px-2"
                                        style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                                    >
                                        {chip.label}
                                        {chip.count !== undefined && (
                                            <Badge
                                                bg={filterChipValue === chip.value ? 'light' : 'secondary'}
                                                text={filterChipValue === chip.value ? 'primary' : undefined}
                                                className="ms-1"
                                                style={{ fontSize: '0.7rem' }}
                                            >
                                                {chip.count}
                                            </Badge>
                                        )}
                                    </Nav.Link>
                                </Nav.Item>
                            ))}
                        </Nav>
                    )}
                    <div className="management-column__list">
                        {loading ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" variant="primary" size="sm" />
                                <p className="text-muted mt-2 mb-0">Laddar...</p>
                            </div>
                        ) : count === 0 ? (
                            <div className="management-column__empty">{emptyMessage}</div>
                        ) : (
                            children
                        )}
                        {/* "Visa alla" lives inside the scrollable list, so it appears at the
                            bottom of the scrolled content rather than being pinned. */}
                        {viewAllLink && count > 20 && (
                            <div className="text-center py-2">
                                <Link to={viewAllLink} className="text-primary">
                                    Visa alla ({count})
                                </Link>
                            </div>
                        )}
                        {onShowAll && count > 20 && (
                            <div className="text-center py-2">
                                <button
                                    onClick={onShowAll}
                                    className="btn btn-link text-primary p-0"
                                    style={{ textDecoration: 'none' }}
                                >
                                    Visa alla ({count})
                                </button>
                            </div>
                        )}
                        {!loading && footer}
                    </div>
                </>
            )}
        </div>
    );
};

export default ColumnCard;
